import { useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, Polyline, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.heat";
import { apiClient } from "../App";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { MapPin, Flame, Play, Pause, RotateCcw } from "lucide-react";

// Numbered divIcon (Leaflet's default icon SVG doesn't show numbers)
const buildNumberedIcon = (n) =>
  L.divIcon({
    className: "",
    html: `<div style="background:#2563eb;color:white;border:2px solid white;border-radius:999px;width:28px;height:28px;display:flex;align-items:center;justify-content:center;font-weight:600;font-size:12px;box-shadow:0 2px 6px rgba(0,0,0,0.3);">${n}</div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
  });

// Auto-fit map to marker bounds
function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points || points.length === 0) return;
    const bounds = L.latLngBounds(points.map((p) => [p.lat, p.lng]));
    if (bounds.isValid()) {
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 16 });
    }
  }, [points, map]);
  return null;
}

// Heat layer driven by [[lat, lng, intensity], ...]
function HeatLayer({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points || points.length === 0) return undefined;
    const layer = L.heatLayer(points, {
      radius: 28,
      blur: 22,
      maxZoom: 17,
      minOpacity: 0.35,
      gradient: { 0.2: "#3b82f6", 0.4: "#10b981", 0.6: "#facc15", 0.8: "#f97316", 1.0: "#ef4444" },
    }).addTo(map);
    const bounds = L.latLngBounds(points.map((p) => [p[0], p[1]]));
    if (bounds.isValid()) map.fitBounds(bounds, { padding: [40, 40], maxZoom: 16 });
    return () => {
      map.removeLayer(layer);
    };
  }, [points, map]);
  return null;
}

const HEAT_PERIODS = [
  { value: "today", label: "Today" },
  { value: "week", label: "7 Days" },
  { value: "month", label: "30 Days" },
  { value: "quarter", label: "Quarter" },
  { value: "year", label: "Year" },
];

export default function ActivityTrailMap({ fundraisers = [], defaultMode = "trail", hideTrailMode = false }) {
  const today = new Date().toISOString().split("T")[0];
  const [mode, setMode] = useState(hideTrailMode ? "heat" : defaultMode); // "trail" | "heat"
  const [fundraiserId, setFundraiserId] = useState("all");
  const [date, setDate] = useState(today);
  const [loading, setLoading] = useState(false);
  const [points, setPoints] = useState([]);
  const reqIdRef = useRef(0);

  useEffect(() => {
    if (mode !== "trail") return;
    const myReq = ++reqIdRef.current;
    const params = new URLSearchParams();
    if (fundraiserId && fundraiserId !== "all") params.append("fundraiser_id", fundraiserId);
    if (date) params.append("date", date);
    setLoading(true);
    apiClient
      .get(`/activity-locations/trail?${params.toString()}`)
      .then((res) => {
        if (myReq !== reqIdRef.current) return;
        setPoints(res.data?.points || []);
      })
      .catch(() => {
        if (myReq !== reqIdRef.current) return;
        setPoints([]);
      })
      .finally(() => {
        if (myReq === reqIdRef.current) setLoading(false);
      });
  }, [fundraiserId, date, mode]);

  // Heat map state + fetch
  const [heatPeriod, setHeatPeriod] = useState("week");
  const [heatPoints, setHeatPoints] = useState([]);
  const [heatMeta, setHeatMeta] = useState({ count: 0, unique: 0 });

  // Playback state (Trail mode only). playbackIndex === null => static, show all points.
  const [playbackIndex, setPlaybackIndex] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playSpeed, setPlaySpeed] = useState(1);
  const playTimerRef = useRef(null);

  // Stop any running timer
  const clearPlayTimer = () => {
    if (playTimerRef.current) {
      clearInterval(playTimerRef.current);
      playTimerRef.current = null;
    }
  };

  // Reset playback whenever the underlying trail dataset changes (filters/date/mode)
  useEffect(() => {
    clearPlayTimer();
    setIsPlaying(false);
    setPlaybackIndex(null);
  }, [points, mode]);

  // Drive playback interval
  useEffect(() => {
    clearPlayTimer();
    if (!isPlaying) return undefined;
    if (!points || points.length === 0) {
      setIsPlaying(false);
      return undefined;
    }
    const baseMs = 1200;
    const intervalMs = Math.max(120, Math.round(baseMs / playSpeed));
    playTimerRef.current = setInterval(() => {
      setPlaybackIndex((prev) => {
        const next = (prev === null ? 0 : prev) + 1;
        if (next >= points.length) {
          clearPlayTimer();
          setIsPlaying(false);
          return points.length;
        }
        return next;
      });
    }, intervalMs);
    return clearPlayTimer;
  }, [isPlaying, playSpeed, points]);

  // Cleanup on unmount
  useEffect(() => clearPlayTimer, []);

  const handlePlay = () => {
    if (!points || points.length === 0) return;
    // If at end (or at null), restart from first point
    if (playbackIndex === null || playbackIndex >= points.length) {
      setPlaybackIndex(1);
    }
    setIsPlaying(true);
  };
  const handlePause = () => setIsPlaying(false);
  const handleReset = () => {
    setIsPlaying(false);
    setPlaybackIndex(null);
  };

  const isPlaybackActive = playbackIndex !== null;
  const visiblePoints = isPlaybackActive ? points.slice(0, playbackIndex) : points;
  const currentPoint =
    isPlaybackActive && playbackIndex > 0 ? points[playbackIndex - 1] : null;

  useEffect(() => {
    if (mode !== "heat") return;
    const myReq = ++reqIdRef.current;
    setLoading(true);
    apiClient
      .get(`/activity-locations/heatmap?period=${heatPeriod}`)
      .then((res) => {
        if (myReq !== reqIdRef.current) return;
        setHeatPoints(res.data?.points || []);
        setHeatMeta({ count: res.data?.count || 0, unique: res.data?.unique || 0 });
      })
      .catch(() => {
        if (myReq !== reqIdRef.current) return;
        setHeatPoints([]);
        setHeatMeta({ count: 0, unique: 0 });
      })
      .finally(() => {
        if (myReq === reqIdRef.current) setLoading(false);
      });
  }, [heatPeriod, mode]);

  const polyline = useMemo(() => visiblePoints.map((p) => [p.lat, p.lng]), [visiblePoints]);
  // Default view: UK (fits activity data) — will be overridden by FitBounds when points exist
  const defaultCenter = [54.5, -3.0];
  const defaultZoom = 6;

  return (
    <Card className="border-slate-200" data-testid="activity-trail-map-card">
      <CardHeader>
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <CardTitle className="flex items-center gap-2">
              {mode === "heat" ? (
                <Flame className="h-5 w-5 text-orange-500" />
              ) : (
                <MapPin className="h-5 w-5 text-blue-600" />
              )}
              {mode === "heat" ? "Operational Coverage Heat Map" : "Fundraiser Trail Map"}
            </CardTitle>
            <p className="text-sm text-slate-500 mt-1">
              {mode === "heat"
                ? "Where fundraising activity is happening — density of conversations and conversions across the organisation."
                : "Visual chronological path for a fundraiser's day."}
            </p>
          </div>
          <div className="flex gap-1" data-testid="map-mode-toggle">
            {!hideTrailMode && (
              <Button
                size="sm"
                variant={mode === "trail" ? "default" : "outline"}
                onClick={() => setMode("trail")}
                data-testid="map-mode-trail"
                className={mode === "trail" ? "bg-blue-600 hover:bg-blue-700 text-white" : ""}
              >
                <MapPin className="h-3.5 w-3.5 mr-1.5" />
                Trail
              </Button>
            )}
            {!hideTrailMode && (
              <Button
                size="sm"
                variant={mode === "heat" ? "default" : "outline"}
                onClick={() => setMode("heat")}
                data-testid="map-mode-heat"
                className={mode === "heat" ? "bg-orange-500 hover:bg-orange-600 text-white" : ""}
              >
                <Flame className="h-3.5 w-3.5 mr-1.5" />
                Heat
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {mode === "trail" && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4" data-testid="trail-map-filters">
              <Select value={fundraiserId} onValueChange={setFundraiserId}>
                <SelectTrigger data-testid="trail-map-fundraiser-select">
                  <SelectValue placeholder="All fundraisers" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All fundraisers</SelectItem>
                  {fundraisers.map((f) => (
                    <SelectItem key={f.id} value={f.id}>
                      {f.name || f.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                data-testid="trail-map-date"
              />
              <div className="flex items-center text-xs text-slate-500 px-1" data-testid="trail-map-status">
                {loading ? "Loading..." : `${points.length} point${points.length === 1 ? "" : "s"} on ${date}`}
              </div>
            </div>

            {/* Playback controls */}
            <div
              className="flex flex-wrap items-center gap-2 mb-3 p-2 rounded-md bg-slate-50 border border-slate-200"
              data-testid="trail-playback-controls"
            >
              <Button
                size="sm"
                variant="default"
                className="bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50"
                onClick={isPlaying ? handlePause : handlePlay}
                disabled={points.length === 0}
                data-testid={isPlaying ? "trail-playback-pause" : "trail-playback-play"}
              >
                {isPlaying ? (
                  <>
                    <Pause className="h-3.5 w-3.5 mr-1.5" />
                    Pause
                  </>
                ) : (
                  <>
                    <Play className="h-3.5 w-3.5 mr-1.5" />
                    Play
                  </>
                )}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={handleReset}
                disabled={points.length === 0 || (!isPlaybackActive && !isPlaying)}
                data-testid="trail-playback-reset"
              >
                <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
                Reset
              </Button>
              <div className="flex items-center gap-1 ml-1" data-testid="trail-playback-speed-group">
                <span className="text-xs text-slate-500 mr-1">Speed</span>
                {[1, 5, 10].map((s) => (
                  <Button
                    key={s}
                    size="sm"
                    variant={playSpeed === s ? "default" : "outline"}
                    onClick={() => setPlaySpeed(s)}
                    data-testid={`trail-playback-speed-${s}x`}
                    className={playSpeed === s ? "bg-slate-900 hover:bg-slate-800 text-white" : ""}
                  >
                    {s}x
                  </Button>
                ))}
              </div>
              <div
                className="text-xs text-slate-600 ml-auto font-mono"
                data-testid="trail-playback-status"
              >
                {points.length === 0
                  ? "No points to play"
                  : isPlaybackActive
                  ? `${Math.min(playbackIndex, points.length)} / ${points.length}${
                      currentPoint
                        ? ` · ${currentPoint.fundraiserName || "Unknown"} · ${
                            currentPoint.timestamp
                              ? new Date(currentPoint.timestamp).toLocaleTimeString()
                              : "—"
                          }`
                        : ""
                    }`
                  : `Ready · ${points.length} point${points.length === 1 ? "" : "s"}`}
              </div>
            </div>

            <div
              className="rounded-lg overflow-hidden border border-slate-200"
              style={{ height: 420, width: "100%" }}
              data-testid="trail-map-container"
            >
              <MapContainer
                center={defaultCenter}
                zoom={defaultZoom}
                style={{ height: "100%", width: "100%" }}
                scrollWheelZoom={true}
              >
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                />
                <FitBounds points={points} />
                {polyline.length > 1 && (
                  <Polyline
                    positions={polyline}
                    pathOptions={{ color: "#2563eb", weight: 3, opacity: 0.7, dashArray: "6 6" }}
                  />
                )}
                {visiblePoints.map((p, idx) => (
                  <Marker
                    key={`trail-${idx}-${p.timestamp}`}
                    position={[p.lat, p.lng]}
                    icon={buildNumberedIcon(p.order || idx + 1)}
                  >
                    <Popup>
                      <div className="text-xs space-y-1" data-testid={`trail-popup-${idx}`}>
                        <div className="font-semibold text-slate-900">
                          #{p.order || idx + 1} — {p.fundraiserName || "Unknown"}
                        </div>
                        <div className="text-slate-600">
                          {p.timestamp ? new Date(p.timestamp).toLocaleString() : "—"}
                        </div>
                        {p.campaignName && <div>Campaign: {p.campaignName}</div>}
                        {p.locationName && <div>Location: {p.locationName}</div>}
                        <div>
                          Source:{" "}
                          <span className="font-mono bg-slate-100 px-1 rounded">
                            {p.source}
                          </span>
                        </div>
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            </div>

            {!loading && points.length === 0 && (
              <p className="text-sm text-slate-400 mt-3" data-testid="trail-map-empty">
                No GPS-tagged activity recorded for this fundraiser on {date}.
              </p>
            )}
          </>
        )}

        {mode === "heat" && (
          <>
            <div className="flex items-center justify-between gap-3 mb-4 flex-wrap" data-testid="heat-map-filters">
              <div className="flex gap-1">
                {HEAT_PERIODS.map((p) => (
                  <Button
                    key={p.value}
                    size="sm"
                    variant={heatPeriod === p.value ? "default" : "outline"}
                    onClick={() => setHeatPeriod(p.value)}
                    data-testid={`heat-period-${p.value}`}
                    className={heatPeriod === p.value ? "bg-orange-500 hover:bg-orange-600 text-white" : ""}
                  >
                    {p.label}
                  </Button>
                ))}
              </div>
              <div className="text-xs text-slate-500" data-testid="heat-map-status">
                {loading
                  ? "Loading…"
                  : `${heatMeta.count} total ping${heatMeta.count === 1 ? "" : "s"} · ${heatMeta.unique} unique hotspot${heatMeta.unique === 1 ? "" : "s"}`}
              </div>
            </div>

            <div
              className="rounded-lg overflow-hidden border border-slate-200"
              style={{ height: 420, width: "100%" }}
              data-testid="heat-map-container"
            >
              <MapContainer
                key="heatmap"
                center={defaultCenter}
                zoom={defaultZoom}
                style={{ height: "100%", width: "100%" }}
                scrollWheelZoom={true}
              >
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                />
                {heatPoints.length > 0 && <HeatLayer points={heatPoints} />}
              </MapContainer>
            </div>

            {/* Legend */}
            <div className="mt-3 flex items-center gap-2 text-xs text-slate-500" data-testid="heat-map-legend">
              <span>Low</span>
              <div className="h-2 flex-1 rounded-full" style={{
                background: "linear-gradient(to right, #3b82f6, #10b981, #facc15, #f97316, #ef4444)"
              }} />
              <span>High</span>
            </div>

            {!loading && heatPoints.length === 0 && (
              <p className="text-sm text-slate-400 mt-3" data-testid="heat-map-empty">
                No GPS-tagged activity recorded in this period.
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
